/**
 * Money presentation (design 9A, ARCH-00 §11). Amounts are stored and
 * exchanged in the clinic's configured currency; for IRR clinics the
 * frontend DISPLAYS Toman (1 Toman = 10 IRR) as a formatting rule only —
 * never storage, never a contract. Any other currency renders as itself.
 * 0.00 renders as the amount it is — never "Free" (ZOY-265 owns that).
 */

import type { Locale } from '../i18n/LocaleProvider';

export function formatMoney(
  amount: string | number | null | undefined,
  locale: Locale,
  currencyCode: string | null | undefined,
): string {
  if (amount === null || amount === undefined || amount === '') return '—';
  const n = typeof amount === 'number' ? amount : Number(amount);
  if (Number.isNaN(n)) return '—';
  const toman = currencyCode === 'IRR';
  const value = toman ? n / 10 : n;
  const num = new Intl.NumberFormat(locale === 'fa' ? 'fa-IR' : 'en-US', {
    maximumFractionDigits: toman ? 0 : 2,
  }).format(value);
  const unit = toman ? (locale === 'fa' ? 'تومان' : 'Toman') : (currencyCode ?? '');
  return unit ? `${num} ${unit}` : num;
}

/**
 * The same display rule as plain values, for a chart axis or an export cell:
 * the number in the DISPLAY unit (Toman for IRR) and that unit's word.
 */
export function displayAmount(amount: number, currencyCode: string | null | undefined): number {
  // Hundredths in, thousandths out — never a binary-float tail in a cell.
  return currencyCode === 'IRR' ? Math.round(amount * 100) / 1000 : amount;
}
export function displayUnit(locale: Locale, currencyCode: string | null | undefined): string {
  return currencyCode === 'IRR' ? (locale === 'fa' ? 'تومان' : 'Toman') : (currencyCode ?? '');
}

/** The unit word an INPUT of stored amounts should carry (storage currency, never Toman). */
export function storageUnit(currencyCode: string | null | undefined): string | undefined {
  return currencyCode ?? undefined;
}
